// assets/tmf-cart.js - TMF · cart extras (port of Little Nest assets/lnd-free-ship.js, extended).
// In the cart drawer AND on the /cart page:
//   1. free US shipping progress bar at the top of the summary ("You're $12.05 away from free US shipping")
//   2. under it, the multi-shirt line ("Add 1 more shirt to save 10%") - display only, the real discount is a
//      Shopify automatic discount; tiers come from the section settings and must match it
//      24/10: rule "items" (data-ship-rule) = FREE US shipping on 2+ items (add-on products do not count) or one
//      item over the amount threshold - display only, the real rate is a Shopify free-shipping discount
//   3. a trust line under the Checkout button (replaces Little Nest's edit of snippets/cart-summary.liquid,
//      so no Horizon core file is touched)
// Horizon morphs the cart on every change and drops nodes it does not know, so a MutationObserver puts
// everything back (debounced to one animation frame). Config = data-* on <tmf-cart-component> rendered by
// sections/tmf-cart-extras.liquid.
import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

const BAR_CLASS = 'tmf-ship';
const TRUST_CLASS = 'tmf-cart-trust';
const DRAWER_OPEN_EVENT = 'theme-drawer:open';
const LOCK_SVG =
  '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="4.5" y="10.5" width="15" height="10" rx="2"/><path d="M8 10.5V7.5a4 4 0 0 1 8 0v3"/></svg>';

/** @extends {Component} */
class TmfCartComponent extends Component {
  /** @type {{ subtotal: number, currency: string, apparel: number, items: number } | null} */
  #cart = null;
  /** @type {MutationObserver | null} */
  #observer = null;
  /** @type {Set<Element>} */
  #observed = new Set();
  #frame = 0;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener(ThemeEvents.cartSectionRestored, this.#onRefresh);
    document.addEventListener(DRAWER_OPEN_EVENT, this.#onRefresh);
    this.#observe();
    this.refresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.removeEventListener(ThemeEvents.cartSectionRestored, this.#onRefresh);
    document.removeEventListener(DRAWER_OPEN_EVENT, this.#onRefresh);
    this.#observer?.disconnect();
    this.#observer = null;
    this.#observed.clear();
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#timer);
    this.#abort?.abort();
    for (const node of document.querySelectorAll(`.${BAR_CLASS}[data-tmf], .${TRUST_CLASS}[data-tmf]`)) node.remove();
  }

  /** Free shipping threshold in cart currency, cents. */
  get #thresholdCents() {
    const usd = Number.parseFloat(this.dataset.threshold ?? '75');
    const rate = Number(/** @type {any} */ (window).Shopify?.currency?.rate) || 1;
    return Math.round((Number.isFinite(usd) && usd > 0 ? usd : 75) * 100 * rate);
  }

  /** "2:10,3:15" -> [{n:2,pct:10},{n:3,pct:15}] sorted by n */
  get #tiers() {
    return String(this.dataset.tiers || '')
      .split(',')
      .map((t) => t.split(':').map((x) => Number.parseInt(x, 10)))
      .filter(([n, pct]) => n > 0 && pct > 0)
      .map(([n, pct]) => ({ n: /** @type {number} */ (n), pct: /** @type {number} */ (pct) }))
      .sort((a, b) => a.n - b.n);
  }

  /** @param {any} event */
  #onCartUpdate = (event) => {
    const done = () => {
      this.#observe();
      this.#schedule(0);
    };
    if (event?.promise) event.promise.then(done, done);
    else done();
  };

  #onRefresh = () => {
    this.#observe();
    this.#schedule(0);
  };

  /** @param {number} delay */
  #schedule(delay) {
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => this.refresh(), delay);
  }

  async refresh() {
    this.#abort?.abort();
    const controller = new AbortController();
    this.#abort = controller;
    try {
      const root = /** @type {any} */ (window).Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, signal: controller.signal });
      if (!response.ok) return;
      const cart = await response.json();
      const types = String(this.dataset.bundleTypes || '')
        .split(',')
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean);
      const addonTypes = String(this.dataset.addonTypes || '')
        .split(',')
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean);
      let apparel = 0;
      let items = 0;
      for (const item of cart.items || []) {
        const type = String(item.product_type || '').toLowerCase();
        if (types.includes(type)) apparel += Number(item.quantity) || 0;
        if (!addonTypes.includes(type)) items += Number(item.quantity) || 0;
      }
      this.#cart = {
        // total_price = after order-level discounts, before shipping: the base Shopify uses for the free rate.
        subtotal: Number(cart.total_price ?? cart.items_subtotal_price ?? 0),
        currency: String(cart.currency || /** @type {any} */ (window).Shopify?.currency?.active || 'USD'),
        apparel,
        items,
      };
      this.#paintAll();
    } catch (error) {
      if (/** @type {any} */ (error)?.name !== 'AbortError') console.warn('[tmf-cart] Cart fetch failed:', error);
    }
  }

  /** Watch the drawer and the cart page section so our nodes come back after Horizon morphs them. */
  #observe() {
    const targets = new Set();
    const drawer = document.getElementById('cart-drawer');
    if (drawer) targets.add(drawer);
    for (const items of document.querySelectorAll('cart-items-component')) {
      targets.add(items.closest('.shopify-section') || items);
    }
    let changed = false;
    for (const t of targets) if (!this.#observed.has(t)) changed = true;
    if (!changed) return;
    this.#observer?.disconnect();
    this.#observed = targets;
    this.#observer = new MutationObserver((mutations) => {
      if (mutations.every(isOwnMutation)) return;
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => {
        const recreated = this.#paintAll();
        if (recreated) this.#schedule(150);
      });
    });
    for (const t of targets) this.#observer.observe(t, { childList: true, subtree: true });
  }

  /** @returns {boolean} true when a node had to be (re)created */
  #paintAll() {
    let created = false;
    if (this.#cart) {
      for (const totals of document.querySelectorAll('.cart-totals')) {
        const anchor = totals.closest('.cart-drawer__summary') || totals.parentElement;
        if (!anchor) continue;
        let bar = /** @type {HTMLElement | null} */ (anchor.querySelector(`:scope > .${BAR_CLASS}`));
        if (!bar) {
          bar = buildBar();
          anchor.prepend(bar);
          created = true;
        }
        this.#paintBar(bar);
      }
    }
    if (this.dataset.trust) {
      for (const ctas of document.querySelectorAll('.cart__ctas')) {
        const next = ctas.nextElementSibling;
        if (next?.classList.contains(TRUST_CLASS)) continue;
        ctas.after(this.#buildTrust());
        created = true;
      }
    }
    return created;
  }

  /** @param {HTMLElement} bar */
  #paintBar(bar) {
    if (!this.#cart) return;
    const { subtotal, currency, apparel, items } = this.#cart;
    const showShip = this.dataset.ship !== 'false';
    const threshold = this.#thresholdCents;
    let left = Math.max(0, threshold - subtotal);
    let pct = Math.min(100, Math.round((subtotal / threshold) * 100));
    /** @type {string | null} */
    let itemsMsg = null;
    if (this.dataset.shipRule === 'items') {
      const need = Math.max(1, Number.parseInt(this.dataset.shipItems || '2', 10) || 2);
      const free = items >= need || (items > 0 && subtotal >= threshold);
      const more = free ? 0 : need - items;
      left = free ? 0 : 1;
      pct = free ? 100 : Math.min(95, Math.round((items / need) * 100));
      if (!free) {
        itemsMsg = (this.dataset.textItems || 'Add [n] more item[s] for FREE US shipping')
          .replace('[n]', String(more))
          .replace('[s]', more > 1 ? 's' : '');
      }
    }
    const text = /** @type {HTMLElement} */ (bar.querySelector(`.${BAR_CLASS}__text`));
    const track = /** @type {HTMLElement} */ (bar.querySelector(`.${BAR_CLASS}__bar`));
    if (showShip) {
      const message =
        itemsMsg !== null
          ? itemsMsg
          : left > 0
          ? (this.dataset.textAway || "You're [amount] away from free US shipping").replace('[amount]', this.#money(left, currency))
          : this.dataset.textUnlocked || "You've unlocked free US shipping";
      if (text.textContent !== message) text.textContent = message;
      const fill = /** @type {HTMLElement | null} */ (track.querySelector('span'));
      if (fill && fill.style.width !== `${pct}%`) fill.style.width = `${pct}%`;
      bar.classList.toggle('is-unlocked', left === 0);
    }
    text.hidden = !showShip;
    track.hidden = !showShip;

    const bundle = /** @type {HTMLElement} */ (bar.querySelector(`.${BAR_CLASS}__bundle`));
    const html = this.#bundleHtml(apparel);
    if (bundle.innerHTML !== html) bundle.innerHTML = html;
    bundle.hidden = html === '';
    bar.hidden = !showShip && html === '';
  }

  /** @param {number} count apparel pieces in the cart */
  #bundleHtml(count) {
    const tiers = this.#tiers;
    if (!tiers.length || count < 1) return '';
    const reached = tiers.filter((t) => count >= t.n).pop();
    const next = tiers.find((t) => count < t.n);
    const b = (/** @type {string} */ s) => `<b>${s}</b>`;
    if (next) {
      const more = next.n - count;
      const tpl = reached
        ? this.dataset.textBundleMore || '[pct_now] off your shirts · add [more] more for [pct_next] off'
        : this.dataset.textBundleStart || 'Add [more] more shirt to save [pct_next] on all shirts';
      return escapeHtml(tpl)
        .replace('[more]', b(String(more)))
        .replace('[pct_next]', b(`${next.pct}%`))
        .replace('[pct_now]', b(`${reached ? reached.pct : 0}%`));
    }
    const tpl = this.dataset.textBundleTop || 'Best deal unlocked: [pct_now] off your shirts';
    return escapeHtml(tpl).replace('[pct_now]', b(`${reached ? reached.pct : 0}%`));
  }

  #buildTrust() {
    const p = document.createElement('p');
    p.className = TRUST_CLASS;
    p.dataset.tmf = '';
    const parts = String(this.dataset.trust || '')
      .split('|')
      .map((s) => s.trim())
      .filter(Boolean);
    // 26/10 QA: items are flex children (gap, no " · " text) so a wrapped line never starts or ends with a lone dot
    p.innerHTML = parts.map((s, i) => `<span class="${TRUST_CLASS}__item">${i === 0 ? LOCK_SVG : ''}${escapeHtml(s)}</span>`).join('');
    return p;
  }

  /** @param {number} cents @param {string} currency */
  #money(cents, currency) {
    const format = this.dataset.moneyFormat;
    if (format) {
      try {
        return formatMoney(cents, format, currency);
      } catch {
        /* fall through */
      }
    }
    return `$${(cents / 100).toFixed(2)}`;
  }
}

function buildBar() {
  const bar = document.createElement('div');
  bar.className = BAR_CLASS;
  bar.dataset.tmf = '';
  bar.setAttribute('role', 'status');
  const text = document.createElement('p');
  text.className = `${BAR_CLASS}__text`;
  text.style.margin = '0';
  const track = document.createElement('div');
  track.className = `${BAR_CLASS}__bar`;
  track.setAttribute('aria-hidden', 'true');
  track.append(document.createElement('span'));
  const bundle = document.createElement('p');
  bundle.className = `${BAR_CLASS}__bundle`;
  bundle.hidden = true;
  bar.append(text, track, bundle);
  return bar;
}

/** @param {string} s */
function escapeHtml(s) {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] || c);
}

/**
 * True when a mutation only touches our own nodes (so painting does not loop).
 * @param {MutationRecord} mutation
 */
function isOwnMutation(mutation) {
  const target = mutation.target instanceof Element ? mutation.target : mutation.target.parentElement;
  if (target?.closest(`.${BAR_CLASS}, .${TRUST_CLASS}`)) return true;
  if (mutation.removedNodes.length > 0) return false;
  const added = [...mutation.addedNodes];
  return (
    added.length > 0 &&
    added.every((n) => n instanceof Element && (n.classList.contains(BAR_CLASS) || n.classList.contains(TRUST_CLASS)))
  );
}

if (!customElements.get('tmf-cart-component')) {
  customElements.define('tmf-cart-component', TmfCartComponent);
}
